import moment from 'moment'
import { RuleStatus, RuleExecutionsResponseDTO, AdAccountDTO } from 'api-models'
import { RuleExecution, DailyExecution } from './types'
import { PostHogEvent, PostHogPath } from 'utils/posthog-events'

export const handleTableSort = (
	key: string,
	setSortColumn: (sort: { key: string; direction: 'asc' | 'desc' }) => void,
	trackEvent: (eventName: string, path: string, properties: any) => void
) => {
	const normalizedKey = key.startsWith('-') ? key.slice(1) : key
	const newDirection = key.startsWith('-') ? 'desc' : 'asc'

	trackEvent(PostHogEvent.CHART_CHANGE_FILTER, PostHogPath.AI_AND_CUSTOMRULES, {
		action: 'sort_table',
		column: normalizedKey,
		direction: newDirection
	})

	setSortColumn({
		key: normalizedKey,
		direction: newDirection
	})
}

export const normalizeDate = (dateValue: any): string | null => {
	if (!dateValue) return null

	if (typeof dateValue === 'string') {
		return dateValue.split('T')[0].trim()
	}

	if (typeof dateValue === 'object' && 'value' in dateValue) {
		return String(dateValue.value).split('T')[0].trim()
	}

	if (moment(dateValue).isValid()) {
		return moment(dateValue).format('YYYY-MM-DD')
	}

	return null
}

export const getDailyCount = (exec: any, targetDate: string): number => {
	if (
		!exec?.dailyCounts ||
		!Array.isArray(exec.dailyCounts) ||
		exec.dailyCounts.length === 0
	) {
		return 0
	}

	const normalizedTargetDate = targetDate.trim()

	const dailyCount = exec.dailyCounts.find((dc: any) => {
		if (!dc || !dc.date) return false
		const dcDate = normalizeDate(dc.date)
		return dcDate === normalizedTargetDate
	})

	return dailyCount && typeof dailyCount.count === 'number'
		? dailyCount.count
		: 0
}

export const RuleStatusMapper = {
	[RuleStatus.ACTIVE]: { color: 'green', label: 'Active' },
	[RuleStatus.PAUSED]: { color: 'gray', label: 'Inactive' },
	[RuleStatus.DELETED]: { color: 'red', label: 'Deleted' }
}

export const processRulesAndExecutions = (
	executionData: RuleExecutionsResponseDTO | undefined,
	_dateRange: { from: Date; to: Date },
	_adAccounts: AdAccountDTO[] | undefined
): {
	rules: RuleExecution[]
	dailyExecutions: DailyExecution[]
	dailyBlockedSources: DailyExecution[]
} => {
	if (
		!executionData ||
		!executionData.rules ||
		!Array.isArray(executionData.rules) ||
		executionData.rules.length === 0
	) {
		return { rules: [], dailyExecutions: [], dailyBlockedSources: [] }
	}

	const mappedRules: RuleExecution[] = executionData.rules.map((exec) => ({
		ruleId: exec.ruleId,
		ruleName: exec.ruleName || exec.ruleId,
		level: exec.level || '',
		status: exec.ruleStatus || RuleStatus.ACTIVE,
		adPlatform: exec.adPlatform,
		executions: exec.totalCount ?? 0,
		dailyCounts: exec.dailyCounts || [],
		blockedSources: exec.blockedSourcesCount ?? 0,
		dailyBlockedSources: exec.dailyBlockedSources || [],
		deletedAt:
			exec.deletedAt ||
			(exec.ruleStatus === RuleStatus.DELETED ? exec.updatedAt : undefined)
	}))

	const buildDailyExecutions = (
		getDailyCounts: (rule: RuleExecution) => Array<{ date: string; count: number }> | undefined
	) => {
		const dateSet = new Set<string>()
		mappedRules.forEach((rule) => {
			const dailyCounts = getDailyCounts(rule)
			if (dailyCounts && Array.isArray(dailyCounts)) {
				dailyCounts.forEach((dc: { date: string; count: number }) => {
					if (dc.date) {
						dateSet.add(dc.date)
					}
				})
			}
		})

		const sortedDates = Array.from(dateSet).sort()

		return sortedDates.map((dateStr) => {
			const dayData: DailyExecution = {
				date: moment(dateStr).format('MMM D')
			}

			mappedRules.forEach((rule) => {
				const dailyCount = getDailyCounts(rule)?.find(
					(dc: { date: string; count: number }) => dc.date === dateStr
				)
				dayData[rule.ruleId] = dailyCount?.count ?? 0
			})

			return dayData
		})
	}

	const dailyExecutions = buildDailyExecutions((rule) => rule.dailyCounts)
	const dailyBlockedSources = buildDailyExecutions((rule) => rule.dailyBlockedSources)

	return { rules: mappedRules, dailyExecutions, dailyBlockedSources }
}

export const getRuleMetricValue = (
	rule: RuleExecution,
	metric: 'executions' | 'blockedSources'
) => {
	return metric === 'executions' ? rule.executions : rule.blockedSources
}

export const getRuleMetricLabel = (metric: 'executions' | 'blockedSources') => {
	return metric === 'executions' ? 'executions' : 'blocked sources'
}

export const createHandleRuleToggle = (
	selectedRules: Set<string>,
	setSelectedRules: (
		rules: Set<string> | ((prev: Set<string>) => Set<string>)
	) => void,
	trackPostHogEvent: (eventName: string, path: string, properties: any) => void
) => {
	return (ruleId: string) => {
		if (selectedRules.has(ruleId)) {
			// Deselecting is always allowed
			setSelectedRules((prev) => {
				const next = new Set(prev)
				next.delete(ruleId)
				return next
			})
			trackPostHogEvent(
				PostHogEvent.CHART_CHANGE_FILTER,
				PostHogPath.AI_AND_CUSTOMRULES,
				{ action: 'deselect_rule', ruleId }
			)
		} else {
			if (selectedRules.size < 5) {
				setSelectedRules((prev) => new Set(prev).add(ruleId))
				trackPostHogEvent(
					PostHogEvent.CHART_CHANGE_FILTER,
					PostHogPath.AI_AND_CUSTOMRULES,
					{ action: 'select_rule', ruleId }
				)
			}
		}
	}
}

export const createAxisBottomFormat = (data: any[], indexKey = 'date') => {
	return (value: string): string => {
		try {
			const parsedDate = moment(value, 'MMM D')
			if (parsedDate.isAfter(moment(), 'day')) {
				parsedDate.subtract(1, 'year')
			}

			const dataIndex = data.findIndex((d) => d[indexKey] === value)
			if (dataIndex === -1) {
				return parsedDate.format('MMM D')
			}

			const totalDays = data.length
			const showInterval =
				totalDays > 20 ? 5 : totalDays > 14 ? 4 : totalDays > 7 ? 3 : 2

			const lastIntervalIndex =
				Math.floor((totalDays - 1) / showInterval) * showInterval
			const gapToEnd = totalDays - 1 - lastIntervalIndex

			if (dataIndex === 0) {
				return parsedDate.format('MMM D')
			}

			if (dataIndex === totalDays - 1 && gapToEnd > showInterval / 2) {
				return parsedDate.format('MMM D')
			}

			if (dataIndex % showInterval === 0) {
				return parsedDate.format('MMM D')
			}

			return ''
		} catch (error) {
			return value
		}
	}
}
